import React from 'react';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';

/**
 * Exported so that any call site marking stream items read can refresh the bell
 * via `refetchQueries`. Prefer the hooks in `notifications_read_state`, which
 * already do this.
 */
export const NOTIFICATIONS_BELL_COUNT_QUERY = gql(`
  query NotificationsBellCount {
    notificationCounts {
      total
    }
  }
`);

export interface NotificationsBellCount {
  /**
   * Total badge count for the bell, summed server-side across the three
   * notification surfaces. `0` until the first response lands, and `0` if that
   * first request fails. A later failed refetch leaves the last good count in
   * place rather than resetting to `0`.
   */
  count: number;
  loading: boolean;
  /** Re-reads the count. Safe to call from event handlers; never rejects. */
  refetch: () => void;
}

/**
 * Owns the treatment bell's badge count, read from CoreQL.
 *
 * Only mount this on the treatment branch of the notifications-center
 * experiment — control users continue to read their count from `/my_counts`,
 * and mounting this hook for them would issue a `NotificationsBellCount`
 * request they never use.
 *
 * The count refreshes when the tab regains visibility, and after either of the
 * mark-read mutations in `notifications_read_state` completes.
 */
export function useNotificationsBellCount(): NotificationsBellCount {
  const { data, loading, refetch } = useQuery(NOTIFICATIONS_BELL_COUNT_QUERY);

  // Apollo's refetch rejects on network failure. A stale bell count is not
  // worth an unhandled rejection, so swallow it and wait for the next refresh.
  const refetchQuietly = React.useCallback(() => {
    refetch().catch(() => {});
  }, [refetch]);

  React.useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        refetchQuietly();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchQuietly]);

  return {
    // Null on a partial success, so the hop is guarded.
    count: data?.notificationCounts?.total ?? 0,
    loading,
    refetch: refetchQuietly,
  };
}
